import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, FileDown, FileText, Pencil, Trash2 } from "lucide-react";
import { api, eur, dt, errMsg, download } from "@/lib/api";
import { PageHeader, Empty } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const Azioni = ({ p, reload }) => {
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const open = p.stato !== "fatturata";
  const run = async (fn) => { setBusy(true); try { await fn(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); } };
  const fattura = () => run(async () => {
    if (!window.confirm(`Trasformare la proforma ${p.numero} in fattura con data di oggi?`)) return;
    const { data } = await api.post(`/proforma/${p.id}/fattura`);
    toast.success(`Creata la fattura ${data.numero} (bozza): controllala e inviala allo SdI`);
    nav(`/app/fatture/${data.invoice_id}`);
  });
  const del = () => run(async () => {
    if (!window.confirm(`Eliminare la proforma ${p.numero}?`)) return;
    await api.delete(`/proforma/${p.id}`); toast.success("Proforma eliminata"); reload();
  });
  return (
    <div className="flex flex-wrap justify-end gap-1">
      <Button size="sm" variant="ghost" disabled={busy} data-testid={`proforma-pdf-${p.id}`} onClick={() => run(() => download(`/proforma/${p.id}/pdf`))}><FileDown className="h-4 w-4" /></Button>
      {open && <Button size="sm" variant="ghost" data-testid={`proforma-edit-${p.id}`} onClick={() => nav(`/app/proforma/${p.id}`)}><Pencil className="h-4 w-4" /></Button>}
      {open && <Button size="sm" className="bg-sky-600 hover:bg-sky-700" disabled={busy} data-testid={`proforma-to-invoice-${p.id}`} onClick={fattura}><FileText className="mr-1 h-4 w-4" />Trasforma in fattura</Button>}
      {open && <Button size="sm" variant="ghost" disabled={busy} data-testid={`proforma-delete-${p.id}`} onClick={del}><Trash2 className="h-4 w-4 text-rose-600" /></Button>}
      {!open && <Link to={`/app/fatture/${p.invoice_id}`} className="px-2 text-sm font-medium text-sky-700 underline" data-testid={`proforma-invoice-link-${p.id}`}>Fattura {p.invoice_numero}</Link>}
    </div>
  );
};

export default function Proforma() {
  const [items, setItems] = useState(null);
  const load = useCallback(() => { api.get("/proforma").then((r) => setItems(r.data)).catch((e) => { toast.error(errMsg(e)); setItems([]); }); }, []);
  useEffect(() => { load(); }, [load]);
  return (
    <div>
      <PageHeader title="Fatture proforma" subtitle="Non vanno allo SdI: quando il cliente paga, trasformala in fattura con un tasto">
        <Button asChild className="bg-sky-600 hover:bg-sky-700" data-testid="proforma-new-button"><Link to="/app/proforma/nuova"><Plus className="mr-2 h-4 w-4" />Nuova proforma</Link></Button>
      </PageHeader>
      {items === null ? null : items.length === 0 ? <Empty text="Nessuna proforma" /> : (
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
          <Table data-testid="proforma-table">
            <TableHeader><TableRow><TableHead>Numero</TableHead><TableHead>Data</TableHead><TableHead>Cliente</TableHead><TableHead className="text-right">Totale</TableHead><TableHead>Stato</TableHead><TableHead /></TableRow></TableHeader>
            <TableBody>
              {items.map((p) => (
                <TableRow key={p.id} data-testid={`proforma-row-${p.id}`}>
                  <TableCell className="font-mono font-semibold">{p.numero}</TableCell>
                  <TableCell>{dt(p.data)}</TableCell>
                  <TableCell>{p.controparte?.denominazione}</TableCell>
                  <TableCell className="text-right font-mono">{eur(p.totals?.totale)}</TableCell>
                  <TableCell><span className={`rounded px-2 py-0.5 text-xs font-semibold ${p.stato === "fatturata" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>{p.stato === "fatturata" ? "Fatturata" : "Da incassare"}</span></TableCell>
                  <TableCell><Azioni p={p} reload={load} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
